import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Settings, User, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const UserProfile = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [avatarLoading, setAvatarLoading] = useState(true);
  const [avatarError, setAvatarError] = useState(false);
  const dropdownRef = useRef(null);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user?.id) return;
      
      try {
        setAvatarLoading(true);
        setAvatarError(false);
        
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (error) throw error;
        
        if (data?.avatar_url) {
          // Verify the avatar still exists in storage
          const { data: existsData, error: existsError } = await supabase.storage
            .from('avatars')
            .createSignedUrl(data.avatar_url.split('avatars/')[1], 10);
            
          if (!existsError && existsData) {
            setProfileData({
              ...data,
              avatar_url: data.avatar_url
            });
          } else {
            setAvatarError(true);
            setProfileData({
              ...data,
              avatar_url: null
            });
          }
        } else {
          setProfileData(data);
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        setAvatarError(true);
      } finally {
        setAvatarLoading(false);
      }
    };

    fetchProfileData();
  }, [user?.id]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const renderAvatar = () => {
    if (avatarLoading) {
      return (
        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
          <Loader2 className="h-4 w-4 text-navy-600 animate-spin" />
        </div>
      );
    }

    if (profileData?.avatar_url && !avatarError) {
      return (
        <img
          src={profileData.avatar_url}
          alt={`${profileData.full_name || 'User'}'s avatar`}
          className="w-8 h-8 rounded-full object-cover border-2 border-gray-100"
          onError={() => setAvatarError(true)}
        />
      );
    }

    return (
      <div className="w-8 h-8 rounded-full border-2 border-gray-100 bg-navy-50 flex items-center justify-center">
        <User className="h-4 w-4 text-navy-600" />
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {renderAvatar()}
        <div className="hidden md:block text-left">
          <div className="font-medium text-gray-700 text-sm">
            {profileData?.full_name || user?.name || 'User'}
          </div>
          <div className="text-xs text-gray-500">
            {formatTime(currentTime)}
          </div>
        </div>
      </button>

      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-100 z-50"
          role="menu"
        >
          <div className="p-4 border-b border-gray-100">
            <div className="font-medium text-gray-900">
              {profileData?.full_name || user?.name || 'User'}
            </div>
            <div className="text-sm text-gray-500">
              {formatDate(currentTime)}
            </div>
          </div>
          
          <div className="py-1">
            <Link
              to="/profile"
              className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
              role="menuitem"
              onClick={() => setIsOpen(false)}
            >
              <User size={16} />
              <span>Profile</span>
            </Link>
            
            <Link
              to="/settings"
              className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
              role="menuitem"
              onClick={() => setIsOpen(false)}
            >
              <Settings size={16} />
              <span>Settings</span>
            </Link>
            
            <button
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors text-left"
              role="menuitem"
            >
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;